import type { ThemePreference } from '@dashboard/modules/settings/composables/useUserSettings'
import { saveUserSettings } from '@dashboard/modules/settings/composables/useUserSettings'
import { useAuthStore } from '@dashboard/stores/authStore'
import { useColorMode } from '@vueuse/core'
import type { UseColorModeReturn } from '@vueuse/core'
import { computed, effectScope, watch } from 'vue'

type ThemeState = {
  mode: UseColorModeReturn
}

let state: ThemeState | null = null

// One color mode for the whole app. It shares useDark()'s storage key and
// class names, so charts that read useDark() follow along. A theme saved on
// the account wins over the browser's stored choice once the user loads.
function themeState(): ThemeState {
  if (state) {
    return state
  }
  const scope = effectScope(true)
  state = scope.run(() => {
    const mode = useColorMode({
      storageKey: 'vueuse-color-scheme',
      modes: { dark: 'dark', light: '' }
    })
    const authStore = useAuthStore()
    watch(
      () => authStore.user?.theme,
      (saved) => {
        if (saved) {
          mode.store.value = saved === 'system' ? 'auto' : saved
        }
      },
      { immediate: true }
    )
    return { mode }
  }) as ThemeState
  return state
}

/**
 * The dashboard theme. The footer toggle and Preferences › Theme are the same
 * setting: both save it to the account (`users.theme`) as well as the browser.
 */
export function useThemePreference() {
  const { mode } = themeState()

  const preference = computed<ThemePreference>(() => (mode.store.value === 'auto' ? 'system' : mode.store.value))
  const isDark = computed(() => mode.state.value === 'dark')

  const setPreference = async (next: ThemePreference) => {
    mode.store.value = next === 'system' ? 'auto' : next
    if (useAuthStore().user) {
      await saveUserSettings({ theme: next })
    }
  }

  return {
    preference,
    isDark,
    setPreference,
    toggle: () => setPreference(isDark.value ? 'light' : 'dark')
  }
}
